import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { MyOrder, MoDocTableData } from 'ts/shared/interfaces/MOResponse';
import Button from 'components/ui/Button/Button';
import useModalClass from 'hooks/useModalClass';
import { getStatus, moDocTable } from 'constants/mo';
import { formatDocTable } from 'utils/shared/my-order';
import MoUploadDocument from './MoUploadDocument';
import MoPrint from './MoPrint';
import MoPNRDocket from './MoPNRDocket';
import MoVoucher from './MoVoucher';
import MoSaveDocket from './MoSaveDocket';
import ScreenshotSupport from '../../ScreenshotSupport';
import MaximizeModal from '../../MaximizeModal';
import Table from '../Table';
import useDraggableModal from 'hooks/useDraggableModal';

interface MoDocumentsModalProps {
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
  myOrder: MyOrder;
  onUpdateMyOrder: (myOrder: MyOrder) => void;
}

const MoDocumentsModal: FC<MoDocumentsModalProps> = ({
  openModal,
  setOpenModal,
  myOrder,
  onUpdateMyOrder,
}) => {
  const { t } = useTranslation();
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const classList = useModalClass(openModal);
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);
  const [docsData, setDocsData] = useState<MoDocTableData[]>([]);

  useEffect(() => {
    if (openModal && myOrder) {
      const onDocumentDeleted = (documentId: number) => {
        setDocsData((prev) => prev.filter((doc) => doc.documentId !== documentId));
        onUpdateMyOrder({
          ...myOrder,
          order: {
            ...myOrder.order,
            documents: myOrder.order.documents.filter(
              (doc) => doc.orderDocumentId !== documentId
            ),
          },
        });
      };
      setDocsData(
        formatDocTable(myOrder.order.documents, myOrder.orderRow.companyId, onDocumentDeleted)
      );
    } else if (!openModal) {
      setDocsData([]);
    }
  }, [openModal, myOrder]); // eslint-disable-line

  const handleUpdateMyOrder = (updatedOrder: MyOrder) => {
    onUpdateMyOrder(updatedOrder);
    // Refresh docs data after upload
    const onDocumentDeleted = (documentId: number) => {
      setDocsData((prev) => prev.filter((doc) => doc.documentId !== documentId));
      onUpdateMyOrder({
        ...updatedOrder,
        order: {
          ...updatedOrder.order,
          documents: updatedOrder.order.documents.filter(
            (doc) => doc.orderDocumentId !== documentId
          ),
        },
      });
    };
    setDocsData(
      formatDocTable(updatedOrder.order.documents, updatedOrder.orderRow.companyId, onDocumentDeleted)
    );
  };

  if (!openModal) return null;

  return (
    <div
      className="modal-overlay modal-overlay-top modal-maximizable" style={{ direction: 'ltr' }}
      ref={modalOverlayRef}
      onClick={(e) => e.target === e.currentTarget && setOpenModal(false)}
    >
      <div ref={modalRef} className={classList} style={{ ...modalStyle, width: '95%', maxWidth: 1200 }}>
        <div
          className={'modal-title  draggable-modal-handle' + (isDragging ? ' dragging' : '')}
          {...dragHandleProps}
        >
          <div>
            <span className="title">
              {t('mo.documents')}
              {' '}-{' '}Order: {myOrder.orderRow.orderId}{' '}
              Segment: {myOrder.fullSegment.orderSegId}{' '}
              {!!myOrder.orderRow.status && (
                <span className={`mo-status ${getStatus(myOrder.orderRow.status)}`}>
                  {myOrder.orderRow.status}
                </span>
              )}
            </span>
          </div>
          <div data-no-drag="true">
            <ScreenshotSupport />
            <MaximizeModal modalOverlayRef={modalOverlayRef} />
            <FontAwesomeIcon icon={faTimes} onClick={() => setOpenModal(false)} />
          </div>
        </div>
        <div className="modal-content">
          <div className='flex-row mb-20' style={{ gap: '5px' }}>
            <MoPrint segment={myOrder.fullSegment} orderRow={myOrder.orderRow} toBottom={true} />
            <MoPNRDocket segment={myOrder.fullSegment} orderRow={myOrder.orderRow} onSuccess={onUpdateMyOrder} />
            <MoVoucher segment={myOrder.fullSegment} myOrder={myOrder} onSuccess={onUpdateMyOrder} />
            <MoSaveDocket myOrder={myOrder} onSuccess={onUpdateMyOrder} />
          </div>
          <div className="mb-20">
            <MoUploadDocument myOrder={myOrder} onSuccess={handleUpdateMyOrder} />
          </div>
          {docsData.length > 0 && (
            <Table data={docsData} columns={moDocTable} />
          )}
        </div>
        <div className="modal-footer">
          <Button
            className="btn btn-default"
            title={t('close')}
            onClick={() => setOpenModal(false)}
          />
        </div>
      </div>
    </div>
  );
};

export default MoDocumentsModal;

